import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';

// 일요일은 수업이 없어 달력에서 뺀다 (월~토 6칸)
const WEEKDAYS = ['월', '화', '수', '목', '금', '토'];
const MAX_CELL_LINES = 3;
const now = new Date();

const TYPECLS_CLASS = {
  정규: 'typecls-regular',
  특별: 'typecls-special',
  보충: 'typecls-supplement',
  방과후: 'typecls-afterschool',
  행사: 'typecls-event',
  휴업: 'typecls-closure',
  기타: 'typecls-etc',
};

function typeclsClassName(typecls) {
  return TYPECLS_CLASS[typecls] || 'typecls-etc';
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toDateStr(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function monthRange(year, month) {
  const lastDay = new Date(year, month, 0).getDate();
  return { start: toDateStr(year, month, 1), end: toDateStr(year, month, lastDay) };
}

function buildWeeks(year, month) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const weeks = [];
  let cur = Array(6).fill(null);
  const flush = () => {
    if (cur.some((c) => c !== null)) weeks.push(cur);
    cur = Array(6).fill(null);
  };
  for (let d = 1; d <= daysInMonth; d += 1) {
    const w = new Date(year, month - 1, d).getDay();
    if (w === 0) continue; // 일요일 제외
    const col = w - 1;
    cur[col] = d;
    if (col === 5) flush();
  }
  flush();
  return weeks;
}

// 달력 칸에 짧게 보여줄 한 줄: 정규는 "1-1 1차"(학년-반 차시), 그 외는 "2-2 방"(수업구분 첫 글자)
function shortLabel(e) {
  const where = e.class_no ? `${e.grade}-${e.class_no}` : `${e.grade}`;
  const what = e.typecls === '정규' ? `${e.unitno ?? ''}차시` : e.typecls.charAt(0);
  return `${where} ${what}`;
}

// 월~목 중 내가 수업한 기록이 없는 날에는 학사일정을 대신 보여준다.
// 같은 일정(수업구분+제목)은 학년을 묶어서 한 줄로 만든다: [{ grades: [1,2], typecls, title }]
function groupAcademic(list) {
  const groups = [];
  const byKey = new Map();
  list.forEach((e) => {
    const key = `${e.typecls}|${e.title || ''}`;
    let g = byKey.get(key);
    if (!g) {
      g = { key, typecls: e.typecls, title: e.title, grades: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    if (!g.grades.includes(e.grade)) g.grades.push(e.grade);
  });
  groups.forEach((g) => g.grades.sort((a, b) => a - b));
  return groups;
}

// 달력 칸(약 5글자)에 들어갈 학사일정 한 줄. 수업 성격(정규/특별/보충/방과후)은 수업구분을,
// 행사/휴업/기타 같은 일정은 제목(예: 기말고사)을 보여주고, 일부 학년만 해당되면 학년을 앞에 붙인다.
// 자세한 내용(시간대 등)은 날짜를 눌러 팝업에서 본다.
const CLASS_TYPES = ['정규', '특별', '보충', '방과후'];
function academicCellText(g) {
  const label = CLASS_TYPES.includes(g.typecls) ? g.typecls : g.title || g.typecls;
  return g.grades.length >= 3 ? label : `${g.grades.join(',')} ${label}`;
}

// 수업현황은 지난 자료만 보여주므로 오늘보다 뒤 날짜는 자료(수업/학사일정)를 비워둔다 (오늘은 포함)
function todayStr() {
  const n = new Date();
  return toDateStr(n.getFullYear(), n.getMonth() + 1, n.getDate());
}

function isAcademicDay(year, month, d) {
  const w = new Date(year, month - 1, d).getDay();
  return w >= 1 && w <= 4; // 월~목
}

export default function MobileClassStatus() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [byDate, setByDate] = useState({});
  const [academicByDate, setAcademicByDate] = useState({});
  const [activeDate, setActiveDate] = useState(null);

  const load = useCallback(() => {
    const { start, end } = monthRange(year, month);
    api.get('/teacher/class-sessions', { params: { start, end } }).then((res) => {
      const grouped = {};
      res.data.forEach((e) => {
        grouped[e.satdate] = grouped[e.satdate] || [];
        grouped[e.satdate].push(e);
      });
      setByDate(grouped);
    });
    api.get('/admin/academic-schedule', { params: { start, end } }).then((res) => {
      const grouped = {};
      res.data.forEach((e) => {
        grouped[e.schedate] = grouped[e.schedate] || [];
        grouped[e.schedate].push(e);
      });
      setAcademicByDate(grouped);
    });
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const goPrevMonth = () => {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const weeks = useMemo(() => buildWeeks(year, month), [year, month]);
  const today = todayStr();

  const popupSessions = activeDate && activeDate <= today ? byDate[activeDate] || [] : [];
  const popupAcademic = useMemo(() => {
    if (!activeDate || activeDate > todayStr()) return [];
    const [y, m, d] = activeDate.split('-').map(Number);
    return isAcademicDay(y, m, d) ? groupAcademic(academicByDate[activeDate] || []) : [];
  }, [activeDate, academicByDate]);

  return (
    <div className="page mobile-page">
      <div className="page-title-row">
        <button className="btn-ghost" type="button" onClick={goPrevMonth}>◀</button>
        <h2>{year}년 {month}월 수업현황</h2>
        <button className="btn-ghost" type="button" onClick={goNextMonth}>▶</button>
      </div>

      <div className="card mob-class-status-card">
        <table className="academic-calendar-table mob-class-status-table">
          <thead>
            <tr>
              {WEEKDAYS.map((w) => (
                <th key={w}>{w}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, wi) => (
              <tr key={wi}>
                {week.map((d, di) => {
                  if (d === null) return <td key={di} className="academic-calendar-empty" />;
                  const dateStr = toDateStr(year, month, d);
                  const future = dateStr > today;
                  const sessions = future ? [] : byDate[dateStr] || [];
                  // 수업이 있으면 수업을, 월~목에 수업이 없으면 학사일정을 칸에 보여준다
                  const items =
                    sessions.length > 0
                      ? sessions.map((e, i) => ({ key: `s${i}`, cls: typeclsClassName(e.typecls), text: shortLabel(e) }))
                      : !future && isAcademicDay(year, month, d)
                        ? groupAcademic(academicByDate[dateStr] || []).map((g) => ({
                            key: g.key,
                            cls: typeclsClassName(g.typecls),
                            text: academicCellText(g),
                          }))
                        : [];
                  const shown = items.length > MAX_CELL_LINES ? items.slice(0, MAX_CELL_LINES - 1) : items;
                  return (
                    <td key={di} className="academic-calendar-day" onClick={() => setActiveDate(dateStr)}>
                      <div className="academic-calendar-daynum">{d}</div>
                      {shown.map((it) => (
                        <div
                          key={it.key}
                          className={`academic-calendar-entry class-status-entry${it.text.length > 8 ? ' class-status-entry-long' : ''} ${it.cls}`}
                        >
                          {it.text}
                        </div>
                      ))}
                      {items.length > MAX_CELL_LINES && (
                        <div className="academic-calendar-entry class-status-entry class-status-more">
                          +{items.length - (MAX_CELL_LINES - 1)}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {activeDate && (
        <div className="modal-overlay" onClick={() => setActiveDate(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{activeDate} 수업</h3>
              <button className="btn-ghost" type="button" onClick={() => setActiveDate(null)}>닫기</button>
            </div>
            {popupSessions.map((e, i) => (
              <div key={i} className="class-status-detail">
                <span className={`academic-calendar-typecls-badge ${typeclsClassName(e.typecls)}`}>{e.typecls}</span>
                <span>
                  {e.grade}학년 {e.class_no ? `${e.class_no}반 ` : ''}{e.classtype === 'M' ? '(면장반) ' : ''}
                  {e.unitno}차시 {[e.subject, e.chapter].filter(Boolean).join(' ')} {e.typecls}수업 {e.hours}시간
                </span>
              </div>
            ))}
            {popupSessions.length === 0 && popupAcademic.length > 0 && (
              <>
                <h4 className="teacher-schedule-section-label">학사일정</h4>
                {popupAcademic.map((g) => (
                  <div key={g.key} className="class-status-detail">
                    <span className={`academic-calendar-typecls-badge ${typeclsClassName(g.typecls)}`}>{g.typecls}</span>
                    <span>{g.grades.join(',')}학년{g.title ? ` ${g.title}` : ''}</span>
                  </div>
                ))}
              </>
            )}
            {popupSessions.length === 0 && popupAcademic.length === 0 && (
              <div className="empty-row">이 날 진행한 수업이 없습니다.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
